import { useState, useEffect, useRef, useCallback, useLayoutEffect } from 'react';
import { 
  Play, Trophy, Users, Star, ArrowRight, CheckCircle, LogIn, 
  Shield, Award, Clock, BookOpen, Target, Zap, Bookmark,
  Mail, Facebook, Instagram, Twitter, Linkedin, X, BarChart3, CreditCard
} from 'lucide-react';
import ErrorReportButton from './ErrorReportButton';
import ImageLightbox from './ImageLightbox';
import PricingSection from './PricingSection';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${API_URL}/api`;

// Helper function to fix image URLs - handles old domains that need to be redirected
const fixImageUrl = (url) => {
  if (!url) return null;
  
  // If already a full http URL
  if (url.startsWith('http')) {
    // If URL is from Cloudflare R2 (images.ehliyet.nu), return as-is
    if (url.includes('images.ehliyet.nu') || url.includes('.r2.dev')) {
      return url;
    }
    
    // List of old domains that need to be redirected to current domain
    const oldDomains = [
      'swedriving.preview.emergentagent.com',
      'teoriprov-3.preview.emergentagent.com',
      'teoriprov-2.preview.emergentagent.com',
      'teoriprov.preview.emergentagent.com',
      'drivingtheory-1.preview.emergentagent.com',
      'drivingtheory.preview.emergentagent.com',
      'turkswedtheory.preview.emergentagent.com',
      'svenskteori.preview.emergentagent.com',
      'ehliyet.preview.emergentagent.com',
      'swedish-driver-test.preview.emergentagent.com',
      'swedish-theory-test-1.preview.emergentagent.com',
      'swedish-theory-test.preview.emergentagent.com',
      'testprep-tr.preview.emergentagent.com',
      'testprep-tr.emergent.host',
      'testtheory.preview.emergentagent.com'
    ];
    
    // Check if URL is from an old domain
    for (const oldDomain of oldDomains) {
      if (url.includes(oldDomain)) {
        // Extract the path after the domain
        try {
          const urlObj = new URL(url);
          const path = urlObj.pathname;
          // Ensure path has /api prefix
          const fixedPath = path.startsWith('/api') ? path : `/api${path}`;
          return `${API_URL}${fixedPath}`;
        } catch (e) {
          // Fallback: extract filename
          const filename = url.split('/').pop();
          return `${API_URL}/api/uploads/${filename}`;
        }
      }
    }
    
    // Fix old format URLs that don't have /api prefix
    if (url.includes('/uploads/') && !url.includes('/api/uploads/')) {
      return url.replace('/uploads/', '/api/uploads/');
    }
    
    return url;
  }
  
  // If relative path
  if (url.startsWith('/api/uploads/')) {
    return `${API_URL}${url}`;
  }
  if (url.startsWith('/uploads/')) {
    return `${API_URL}/api${url}`;
  }
  
  return `${API_URL}/api/uploads/${url}`;
};

const SAMPLE_QUESTIONS = [
  {
    id: 1,
    question_text: "İsveç'te sağdan gelen araçlara öncelik vermek gerekir. Bu kural ne zaman geçerlidir?",
    choices: [
      { letter: "A", text: "Sadece trafik ışıklarının olmadığı kavşaklarda" },
      { letter: "B", text: "Tüm kavşaklarda, trafik işaretleri aksini belirtmedikçe" },
      { letter: "C", text: "Sadece şehir dışında" },
      { letter: "D", text: "Sadece park alanlarında" }
    ],
    correct_answer: "B",
    explanation: "İsveç'te 'Högerregeln' (sağdan gelene öncelik kuralı) çok önemlidir ve tüm kavşaklarda geçerlidir, tabela veya trafik ışıkları aksini belirtmedikçe."
  },
  {
    id: 2,
    question_text: "Kışın kar yağdığında araç kullanırken en önemli güvenlik önlemi nedir?",
    choices: [
      { letter: "A", text: "Hızı artırmak" },
      { letter: "B", text: "Kış lastiklerini kullanmak ve mesafeyi artırmak" },
      { letter: "C", text: "Sadece ana yollarda gitmek" },
      { letter: "D", text: "Farları kapatmak" }
    ],
    correct_answer: "B",
    explanation: "Kışın kar ve buzlu yollarda kış lastikleri zorunludur ve araçlar arası mesafe artırılmalıdır çünkü fren mesafesi uzar."
  },
  {
    id: 3,
    question_text: "Alkollü içki alındıktan sonra ne kadar süre araç kullanmak yasaktır?",
    choices: [
      { letter: "A", text: "2 saat" },
      { letter: "B", text: "Kan alkol seviyesi 0.2 promil'in altına inene kadar" },
      { letter: "C", text: "Sadece gece vakti" },
      { letter: "D", text: "24 saat" }
    ],
    correct_answer: "B",
    explanation: "İsveç'te kan alkol seviyesi 0.2 promil ve üzerinde araç kullanmak yasaktır. Bu seviye kişiden kişiye değişir ama genellikle bir içkiden sonra bile aşılabilir."
  },
  {
    id: 4,
    question_text: "Çevre dostu sürüş (Eco-driving) için hangi teknik önerilir?",
    choices: [
      { letter: "A", text: "Yüksek devirlerde sürmek" },
      { letter: "B", text: "Yüksek viteste (düşük devirde) sabit hızda gitmek ve ani fren/gaz kullanmaktan kaçınmak" },
      { letter: "C", text: "Sadece otoyollarda kullanmak" },
      { letter: "D", text: "Motor ısınıncaya kadar beklemek" }
    ],
    correct_answer: "B",
    explanation: "Eco-driving için en etkili teknik, yüksek viteste (düşük devirde) sabit hızda sürmektir. Bu yöntem motorun daha verimli çalışmasını sağlar ve yakıt tüketimini minimize eder. Düşük vites kullanmak motoru yüksek devirde çalıştırır ve daha fazla yakıt harcar."
  },
  {
    id: 5,
    question_text: "Bisikletli veya yaya için yol verme kuralı nedir?",
    choices: [
      { letter: "A", text: "Sadece yaya geçitlerinde durulur" },
      { letter: "B", text: "Hem yaya geçitlerinde hem de bisiklet yollarını geçerken yavaşlanır ve gerekirse durulur" },
      { letter: "C", text: "Bisikletliler her zaman bekler" },
      { letter: "D", text: "Hiçbir zaman durulmaz" }
    ],
    correct_answer: "B",
    explanation: "İsveç'te yaya ve bisikletlilere özel önem verilir. Geçitlerde ve bisiklet yolu kesişimlerinde dikkatli olunmalı ve gerektiğinde durulmalıdır."
  },
  {
    id: 6,
    question_text: "Hız sınırı 50 km/sa olan bir yolda kaç metre mesafede durabilirsiniz? (Normal fren durumu)",
    choices: [
      { letter: "A", text: "10 metre" },
      { letter: "B", text: "Yaklaşık 28 metre (reaksiyon + fren mesafesi)" },
      { letter: "C", text: "50 metre" },
      { letter: "D", text: "100 metre" }
    ],
    correct_answer: "B",
    explanation: "50 km/sa hızda toplam durma mesafesi yaklaşık 28 metredir (reaksiyon mesafesi + fren mesafesi). Bu mesafe yol koşullarına göre değişir."
  },
  {
    id: 7,
    question_text: "Emniyet kemeri kullanımı kimler için zorunludur?",
    choices: [
      { letter: "A", text: "Sadece sürücü" },
      { letter: "B", text: "Sürücü ve ön koltuk yolcusu" },
      { letter: "C", text: "Araçtaki tüm yolcular" },
      { letter: "D", text: "Sadece otoyolda" }
    ],
    correct_answer: "C",
    explanation: "İsveç'te emniyet kemeri kullanımı araçtaki tüm yolcular için zorunludur. Çocuklar için uygun yaşa göre çocuk koltuğu kullanılmalıdır."
  },
  {
    id: 8,
    question_text: "Cep telefonu kullanımı ile ilgili kural nedir?",
    choices: [
      { letter: "A", text: "Sürüş sırasında el ile tutarak konuşmak yasaktır" },
      { letter: "B", text: "Sadece mesaj yazmak yasaktır" },
      { letter: "C", text: "Her türlü kullanım serbesttir" },
      { letter: "D", text: "Sadece otoyolda yasaktır" }
    ],
    correct_answer: "A",
    explanation: "Sürüş sırasında cep telefonunu el ile tutarak kullanmak yasaktır. Ancak handsfree (eller serbest) sistemler kullanılabilir."
  },
  {
    id: 9,
    question_text: "Bir yola girerken hangi işaretlemeye dikkat etmelisiniz?",
    choices: [
      { letter: "A", text: "Sadece trafik ışıkları" },
      { letter: "B", text: "Yol işaretleri, tabelalar ve yol çizgileri" },
      { letter: "C", text: "Sadece diğer araçlar" },
      { letter: "D", text: "Hiçbiri" }
    ],
    correct_answer: "B",
    explanation: "Yola girerken tüm işaretlemelere (tabelalar, yol çizgileri, trafik ışıkları) dikkat edilmelidir. Bunlar öncelik sırasını ve güvenli geçişi belirler."
  },
  {
    id: 10,
    question_text: "Park ederken engelli park yerine kim park edebilir?",
    choices: [
      { letter: "A", text: "Sadece 10 dakikalığına herkes" },
      { letter: "B", text: "Sadece geçerli engelli kartı olan kişiler" },
      { letter: "C", text: "Yaşlı sürücüler" },
      { letter: "D", text: "Herkes, yer boşsa" }
    ],
    correct_answer: "B",
    explanation: "Engelli park yerleri sadece geçerli engelli park kartı olan kişiler için ayrılmıştır. Bu kurala uymamak ciddi para cezasına neden olur."
  }
];

const TESTIMONIALS = [
  {
    name: "Mehmet Yılmaz",
    location: "Stockholm",
    rating: 5,
    text: "Ehliyet.nu sayesinde ilk denemede sınavı geçtim! Sorular gerçek sınava çok benziyor.",
    date: "Son Ay"
  },
  {
    name: "Ayşe Demir",
    location: "Göteborg",
    rating: 5,
    text: "Türkçe olması ve detaylı açıklamaları çok yardımcı oldu. Kesinlikle tavsiye ederim!",
    date: "Geçen Ay"
  },
  {
    name: "Can Öztürk",
    location: "Malmö",
    rating: 5,
    text: "Pratik sorular ve sınav modu gerçekten işime yaradı. 5 yıldız!",
    date: "Son Ay"
  },
  {
    name: "Zeynep Kaya",
    location: "Uppsala",
    rating: 5,
    text: "Ücretsiz deneme soruları ile test ettim, çok beğendim ve hemen üye oldum.",
    date: "Geçen Ay"
  },
  {
    name: "Emre Şahin",
    location: "Linköping",
    rating: 5,
    text: "Kategori bazlı çalışma özelliği harika! Zayıf olduğum konulara odaklanarak başarıma çok katkı sağladı.",
    date: "Son Ay"
  },
  {
    name: "Selin Arslan",
    location: "Örebro",
    rating: 5,
    text: "Hem mobilde hem bilgisayarda kullanımı çok rahat. Açıklamalar gerçekten çok detaylı ve öğretici!",
    date: "Son Ay"
  }
];

const FEATURES = [
  {
    icon: BookOpen,
    title: "2000 Güncel Soru",
    description: "Trafikverket sınav sistemine uygun, detaylı açıklamalı sorular"
  },
  {
    icon: Trophy,
    title: "Sınav Modu",
    description: "70 soruluk gerçek sınav deneyimi, zamanlı ve zamansız seçenekler"
  },
  {
    icon: Target,
    title: "Pratik Modu",
    description: "Kategori bazlı çalışma, eksik konularınızı tamamlayın"
  },
  {
    icon: Clock,
    title: "İlerleme Takibi",
    description: "Başarı oranınızı izleyin, gelişiminizi görün"
  },
  {
    icon: Shield,
    title: "Güvenli Ödeme",
    description: "Stripe ile güvenli ödeme, esnek paket seçenekleri"
  },
  {
    icon: Zap,
    title: "Anında Başlangıç",
    description: "Google ile hızlı giriş, dakikalar içinde başlayın"
  }
];

export default function HomePage({ onLogin, onPremium }) {
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [userAnswers, setUserAnswers] = useState({});
  const [showExplanation, setShowExplanation] = useState(false);
  const [showQuestions, setShowQuestions] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [showMarkedList, setShowMarkedList] = useState(false);
  const [showOverview, setShowOverview] = useState(false);
  const [showCookieBanner, setShowCookieBanner] = useState(false);
  const [totalQuestions, setTotalQuestions] = useState(2000); // Default, will be updated
  const [freeQuestionCount, setFreeQuestionCount] = useState(50);
  const [quizQuestions, setQuizQuestions] = useState([]); // Dynamic questions from API
  const [loadingQuestions, setLoadingQuestions] = useState(false);
  const [testimonials, setTestimonials] = useState(TESTIMONIALS); // Dynamic testimonials from API
  const [blogPosts, setBlogPosts] = useState([]); // Dynamic blog posts from API
  const [homepageSettings, setHomepageSettings] = useState(null); // WYSIWYG settings from admin
  const [footerSettings, setFooterSettings] = useState(null); // Footer settings from admin
  const [markedQuestions, setMarkedQuestions] = useState(() => {
    // Load from localStorage
    const saved = localStorage.getItem('markedQuestions');
    return saved ? JSON.parse(saved) : [];
  });
  
  // Ref for quiz container to prevent scroll jump
  const quizContainerRef = useRef(null);
  const scrollPositionRef = useRef(0);
  const isRestoringScroll = useRef(false);
  const isTouchDevice = useRef(false);
  
  // Fetch homepage settings from admin WYSIWYG editor
  useEffect(() => {
    const fetchHomepageSettings = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/homepage/settings`);
        const data = await response.json();
        if (data && Object.keys(data).length > 0) {
          setHomepageSettings(data);
          console.log('🏠 Homepage settings loaded from admin');
        }
      } catch (error) {
        console.error('Homepage settings fetch error:', error);
      }
    };
    fetchHomepageSettings();
  }, []);

  // Fetch footer settings from admin
  useEffect(() => {
    const fetchFooterSettings = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/footer-settings`);
        const data = await response.json();
        if (data && Object.keys(data).length > 0) {
          setFooterSettings(data);
          console.log('🦶 Footer settings loaded from admin');
        }
      } catch (error) {
        console.error('Footer settings fetch error:', error);
      }
    };
    fetchFooterSettings();
  }, []);
  
  // Detect touch device
  useEffect(() => {
    isTouchDevice.current = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
  }, []);

  // SEO: Set document title for better Google ranking
  useEffect(() => {
    document.title = 'İsveç Ehliyet Sınavı Türkçe Hazırlık Soruları 2026 | Ehliyet.nu';
    
    // Update meta description dynamically
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'İsveç ehliyet sınavı Türkçe hazırlık. 2000+ güncel soru, %95 başarı oranı. Ücretsiz deneme, Türkçe açıklamalar. Teoriprov hazırlık.');
    }
  }, []);
  
  // Save scroll position before any state update
  const saveScrollPosition = useCallback(() => {
    scrollPositionRef.current = window.scrollY || window.pageYOffset || document.documentElement.scrollTop;
  }, []);
  
  // Restore scroll position after state update - robust for mobile
  const restoreScrollPosition = useCallback(() => {
    if (isRestoringScroll.current) return;
    isRestoringScroll.current = true;
    
    const targetScroll = scrollPositionRef.current;
    
    // For mobile: temporarily lock body scroll, then restore
    const lockAndRestore = () => {
      // Store original styles
      const originalOverflow = document.body.style.overflow;
      const originalPosition = document.body.style.position;
      const originalTop = document.body.style.top;
      const originalWidth = document.body.style.width;
      
      // Lock scroll momentarily
      if (isTouchDevice.current) {
        document.body.style.overflow = 'hidden';
        document.body.style.position = 'fixed';
        document.body.style.top = `-${targetScroll}px`;
        document.body.style.width = '100%';
      }
      
      // Restore after brief delay
      setTimeout(() => {
        if (isTouchDevice.current) {
          document.body.style.overflow = originalOverflow;
          document.body.style.position = originalPosition;
          document.body.style.top = originalTop;
          document.body.style.width = originalWidth;
        }
        window.scrollTo(0, targetScroll);
        isRestoringScroll.current = false;
      }, 50);
    };
    
    // Execute scroll restoration
    window.scrollTo(0, targetScroll);
    requestAnimationFrame(() => {
      window.scrollTo(0, targetScroll);
      if (isTouchDevice.current) {
        lockAndRestore();
      } else {
        setTimeout(() => {
          window.scrollTo(0, targetScroll);
          isRestoringScroll.current = false;
        }, 100);
      }
    });
  }, []);

  // Fetch real stats from API and quiz settings
  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/stats`);
        const data = await response.json();
        // Always show at least 2000 for marketing purposes
        setTotalQuestions(Math.max(2000, data.total_questions));
      } catch (error) {
        console.error('Stats fetch error:', error);
      }
    };

    const fetchQuizSettings = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/quiz-settings`);
        if (!response.ok) throw new Error('Quiz settings API error');
        const data = await response.json();
        console.log('🎯 Quiz Settings API Response:', data);
        const count = data.free_question_count || 40;
        setFreeQuestionCount(count);
        console.log('✅ Free question count set to:', count);
      } catch (error) {
        console.error('Quiz settings fetch error:', error);
        // Set default to 40 on error
        setFreeQuestionCount(50);
      }
    };

    const fetchTestimonials = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/testimonials`);
        const data = await response.json();
        if (data && data.length > 0) {
          setTestimonials(data);
        }
      } catch (error) {
        console.error('Testimonials fetch error:', error);
        // Fallback to static testimonials
      }
    };

    // Fetch published blog posts for homepage
    const fetchBlogPosts = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/blog/posts`);
        const data = await response.json();
        // Filter only published and featured posts, limit to 6 for homepage
        const publishedPosts = (data.posts || [])
          .filter(post => post.is_published)
          .slice(0, 6);
        setBlogPosts(publishedPosts);
        console.log('📝 Blog posts loaded:', publishedPosts.length);
      } catch (error) {
        console.error('Blog posts fetch error:', error);
      }
    };

    fetchStats();
    fetchQuizSettings();
    fetchTestimonials();
    fetchBlogPosts();
  }, []);

  // Debug log to check state updates
  useEffect(() => {
    console.log('📊 Current freeQuestionCount state:', freeQuestionCount);
  }, [freeQuestionCount]);

  // Check cookie consent on mount - use backend API
  useEffect(() => {
    const checkCookieConsent = async () => {
      try {
        // First check localStorage as quick check
        const localConsent = localStorage.getItem('cookieConsent');
        if (localConsent === 'true') {
          setShowCookieBanner(false);
          return;
        }
        
        // Check browser cookie
        const cookieConsent = document.cookie.split('; ').find(row => row.startsWith('cookie_consent='));
        if (cookieConsent) {
          localStorage.setItem('cookieConsent', 'true');
          setShowCookieBanner(false);
          return;
        }
        
        // Check backend API
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/cookie-consent/check`, {
          credentials: 'include'
        });
        const data = await response.json();
        
        if (data.has_consent) {
          localStorage.setItem('cookieConsent', 'true');
          setShowCookieBanner(false);
        } else {
          setShowCookieBanner(true);
        }
      } catch (error) {
        console.error('Cookie consent check error:', error);
        // Fallback to localStorage
        const consent = localStorage.getItem('cookieConsent');
        if (!consent) {
          setShowCookieBanner(true);
        }
      }
    };
    
    checkCookieConsent();
  }, []);

  // Check URL parameters for actions (e.g., ?action=login)
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const action = urlParams.get('action');
    
    if (action === 'login' && onLogin) {
      // Trigger login modal
      onLogin();
      // Clean up URL
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [onLogin]);

  const acceptCookies = async () => {
    try {
      // Save to backend
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/cookie-consent`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        console.log('🍪 Cookie consent saved to backend');
      }
    } catch (error) {
      console.error('Cookie consent save error:', error);
    }
    
    // Also save to localStorage as backup
    localStorage.setItem('cookieConsent', 'true');
    setShowCookieBanner(false);
  };

  const handleAnswer = (answer) => {
    // Save scroll position FIRST
    saveScrollPosition();
    
    setUserAnswers({ ...userAnswers, [currentQuestion]: answer });
    setShowExplanation(true);
    
    // Restore scroll position after React updates DOM
    restoreScrollPosition();
  };

  // Extra scroll protection when showExplanation changes (answer selection)
  useLayoutEffect(() => {
    // Only restore scroll when answer is selected (showExplanation becomes true)
    if (showExplanation && scrollPositionRef.current > 0) {
      restoreScrollPosition();
    }
  }, [showExplanation, restoreScrollPosition]);

  const toggleBookmark = (questionId) => {
    let updated;
    if (markedQuestions.includes(questionId)) {
      // Remove
      updated = markedQuestions.filter(id => id !== questionId);
    } else {
      // Add
      updated = [...markedQuestions, questionId];
    }
    setMarkedQuestions(updated);
    localStorage.setItem('markedQuestions', JSON.stringify(updated));
  };

  const isMarked = (questionId) => {
    return markedQuestions.includes(questionId);
  };

  const nextQuestion = () => {
    const activeQuestions = quizQuestions.length > 0 ? quizQuestions : SAMPLE_QUESTIONS.slice(0, freeQuestionCount);
    
    if (currentQuestion < activeQuestions.length - 1) {
      // Calculate absolute target position BEFORE state change
      let targetScrollTop = window.scrollY;
      if (quizContainerRef.current) {
        const rect = quizContainerRef.current.getBoundingClientRect();
        targetScrollTop = window.scrollY + rect.top - 100; // 100px offset from top
      }
      
      setCurrentQuestion(currentQuestion + 1);
      setShowExplanation(false);
      
      // Use multiple timing strategies to ensure scroll works
      const scrollToQuiz = () => {
        window.scrollTo(0, Math.max(0, targetScrollTop));
      };
      
      // Immediate
      scrollToQuiz();
      // After microtask
      Promise.resolve().then(scrollToQuiz);
      // After paint
      requestAnimationFrame(scrollToQuiz);
      // After layout
      requestAnimationFrame(() => requestAnimationFrame(scrollToQuiz));
      // Delayed fallbacks
      setTimeout(scrollToQuiz, 50);
      setTimeout(scrollToQuiz, 100);
      setTimeout(scrollToQuiz, 200);
    } else {
      // Son soru - sonuçları göster
      setShowResults(true);
    }
  };

  const prevQuestion = () => {
    if (currentQuestion > 0) {
      // Calculate absolute target position BEFORE state change
      let targetScrollTop = window.scrollY;
      if (quizContainerRef.current) {
        const rect = quizContainerRef.current.getBoundingClientRect();
        targetScrollTop = window.scrollY + rect.top - 100;
      }
      
      setCurrentQuestion(currentQuestion - 1);
      setShowExplanation(false);
      
      // Use multiple timing strategies
      const scrollToQuiz = () => {
        window.scrollTo(0, Math.max(0, targetScrollTop));
      };
      
      scrollToQuiz();
      Promise.resolve().then(scrollToQuiz);
      requestAnimationFrame(scrollToQuiz);
      requestAnimationFrame(() => requestAnimationFrame(scrollToQuiz));
      setTimeout(scrollToQuiz, 50);
      setTimeout(scrollToQuiz, 100);
      setTimeout(scrollToQuiz, 200);
    }
  };

  const restartQuiz = async () => {
    setCurrentQuestion(0);
    setUserAnswers({});
    setShowExplanation(false);
    setShowResults(false);
    setShowOverview(false);
    await loadFreeQuizQuestions();
  };

  // Sınavı bitir ve sonuçları göster
  const finishQuiz = () => {
    setShowResults(true);
    setShowOverview(false);
  };

  // Genel bakış modunu aç/kapat
  const toggleOverview = () => {
    setShowOverview(!showOverview);
  };

  // Genel bakıştan soruya git
  const goToQuestion = (index) => {
    setCurrentQuestion(index);
    setShowOverview(false);
    setShowExplanation(!!userAnswers[index]);
  };

  const loadFreeQuizQuestions = async () => {
    setLoadingQuestions(true);
    try {
      // ALWAYS use homepage-questions endpoint (same as admin panel)
      const response = await fetch(`${API}/homepage-questions`);
      const data = await response.json();
      
      if (data && Array.isArray(data) && data.length > 0) {
        // Relaxed validation - only require question text and at least 1 valid choice
        const validQuestions = data.filter(q => 
          q.question_text && 
          q.choices && 
          Array.isArray(q.choices) && 
          q.choices.length > 0 &&
          q.choices.some(c => c.text && c.text.trim() !== '') // At least one valid choice
        );
        
        if (validQuestions.length > 0) {
          setQuizQuestions(validQuestions);
          console.log(`✅ Loaded ${validQuestions.length} valid homepage questions`);
        } else {
          // No valid questions found
          console.warn('⚠️ No valid questions found, using sample questions');
          setQuizQuestions(SAMPLE_QUESTIONS.slice(0, freeQuestionCount));
        }
      } else {
        console.warn('⚠️ Empty homepage questions, using sample questions');
        setQuizQuestions(SAMPLE_QUESTIONS.slice(0, freeQuestionCount));
      }
    } catch (error) {
      console.error('Failed to load quiz questions:', error);
      // Fallback to sample questions if API fails
      setQuizQuestions(SAMPLE_QUESTIONS.slice(0, freeQuestionCount));
    } finally {
      setLoadingQuestions(false);
    }
  };

  // Load quiz questions when component mounts or freeQuestionCount changes
  useEffect(() => {
    if (freeQuestionCount > 0) {
      loadFreeQuizQuestions();
    }
  }, [freeQuestionCount]);

  // Sonuçları hesapla
  const calculateResults = () => {
    let correct = 0;
    let wrong = 0;
    const activeQuestions = quizQuestions.length > 0 ? quizQuestions : SAMPLE_QUESTIONS.slice(0, freeQuestionCount);
    
    activeQuestions.forEach((q, index) => {
      if (userAnswers[index]) {
        if (userAnswers[index] === q.correct_answer) {
          correct++;
        } else {
          wrong++;
        }
      }
    });
    
    return { correct, wrong, total: activeQuestions.length, unanswered: activeQuestions.length - correct - wrong };
  };

  const activeQuestions = quizQuestions.length > 0 ? quizQuestions : SAMPLE_QUESTIONS.slice(0, freeQuestionCount);
  const question = activeQuestions[currentQuestion];
  const userAnswer = userAnswers[currentQuestion];
  const isCorrect = userAnswer === question.correct_answer;

  return (
    <div className="min-h-screen bg-white relative overflow-hidden">
      {/* Animated Background Elements - Disabled for clean white look */}
      {/* <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 left-0 w-96 h-96 bg-gradient-to-br from-red-500/20 to-orange-500/20 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute top-1/4 right-0 w-96 h-96 bg-gradient-to-br from-blue-500/20 to-cyan-500/20 rounded-full blur-3xl animate-pulse animation-delay-2000"></div>
        <div className="absolute bottom-0 left-1/3 w-96 h-96 bg-gradient-to-br from-purple-500/20 to-pink-500/20 rounded-full blur-3xl animate-pulse animation-delay-4000"></div>
      </div> */}
      
      <div className="relative z-10">

      {/* Hero Section with Red Background */}
      <div 
        className="relative min-h-screen bg-cover bg-center"
        style={{
          backgroundImage: `linear-gradient(rgba(215, 0, 0, 0.85), rgba(180, 0, 0, 0.85)), url('https://images.pexels.com/photos/39080/stop-shield-traffic-sign-road-sign-39080.jpeg')`,
          backgroundBlendMode: 'multiply'
        }}
      >
        {/* Hero background overlay - limited to hero section only */}
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-red-900/50 to-red-950" style={{height: '100vh'}}></div>
        
        
        {/* Swedish Flag - Left Side - BIGGER */}
        <div className="hidden md:block absolute left-8 top-64 z-20 rounded-xl overflow-hidden shadow-2xl border-4 border-white/80 hover:scale-110 transition-transform duration-300" style={{width: '180px', height: '120px'}}>
          <svg viewBox="0 0 16 10" className="w-full h-full">
            {/* Blue background */}
            <rect width="16" height="10" fill="#006AA7"/>
            {/* Yellow cross - vertical */}
            <rect x="5" y="0" width="2" height="10" fill="#FECC00"/>
            {/* Yellow cross - horizontal */}
            <rect x="0" y="4" width="16" height="2" fill="#FECC00"/>
          </svg>
        </div>
        
        {/* Turkish Flag - Right Side - BIGGER */}
        <div className="hidden md:block absolute right-8 top-64 z-20 rounded-xl overflow-hidden shadow-2xl border-4 border-white/80 hover:scale-110 transition-transform duration-300" style={{width: '180px', height: '120px', background: '#E30A17'}}>
          <div className="relative w-full h-full flex items-center justify-center">
            <div className="absolute" style={{left: '22%', top: '50%', transform: 'translateY(-50%)'}}>
              <div className="relative">
                {/* White circle for crescent */}
                <div className="w-14 h-14 rounded-full bg-white"></div>
                {/* Red circle to create crescent */}
                <div className="absolute top-1.5 left-3 w-11 h-11 rounded-full" style={{background: '#E30A17'}}></div>
              </div>
            </div>
            {/* Star */}
            <div className="absolute text-white" style={{right: '26%', top: '50%', transform: 'translateY(-50%)', fontSize: '48px'}}>★</div>
          </div>
        </div>
        
        <div className="relative z-10 container mx-auto px-4 py-12">
          {/* Top Navigation Bar - Badge Left, Login Right */}
          <div className="flex items-center justify-between mb-8">
            {/* Left - Official Exam Format Badge */}
            <div className="flex items-center gap-2 px-3 py-2 sm:px-4 sm:py-2 md:px-6 md:py-3 bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg">
              <span className="text-white font-semibold text-xs sm:text-sm md:text-base">
                {homepageSettings?.hero?.badge_text || "İsveç'in #1 Türkçe Ehliyet Platformu"}
              </span>
            </div>
            
            {/* Right - Login Button */}
            <div className="flex items-center gap-2 sm:gap-3">
              <button
                onClick={onLogin}
                className="flex items-center gap-2 px-3 py-2 sm:px-4 sm:py-2 bg-white/10 hover:bg-white/20 backdrop-blur-sm border border-white/30 rounded-xl text-white font-semibold transition-all text-xs sm:text-sm md:text-base"
              >
                <LogIn className="w-4 h-4 sm:w-5 sm:h-5" />
                <span>Giriş Yap</span>
              </button>
            </div>
          </div>

          {/* Mega Header */}
          <div className="text-center mb-12 space-y-8">
            {/* Main Title - SEO H1 */}
            <h1 className="text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-bold text-white leading-tight tracking-tight px-4">
              {homepageSettings?.hero?.title || "İsveç Ehliyet Sınavı Türkçe Hazırlık Soruları 2026"}
            </h1>
            
            {/* Subtitle */}
            <div className="space-y-2 md:space-y-3 px-4">
              <p className="text-xl sm:text-2xl md:text-3xl font-semibold text-white/95">
                {homepageSettings?.hero?.subtitle || "İsveç Ehliyet Sınavı Türkçe Hazırlık"}
              </p>
              <p className="text-base sm:text-lg md:text-xl text-white/80">
                {homepageSettings?.hero?.subtitle_secondary || "Teori Sınavı • Ehliyet Testi • Trafik Kuralları Türkçe"}
              </p>
            </div>
            
            {/* Description */}
            <p className="text-sm sm:text-base md:text-lg text-white/80 max-w-2xl mx-auto font-normal leading-relaxed px-4">
              {homepageSettings?.hero?.description || "2,000+ güncel ehliyet sorusu • İsveç teori sınavı Türkçe • Ücretsiz deneme • %95 başarı garantisi"}
            </p>
            
            {/* Feature Highlights - Professional */}
            <div className="mt-8 md:mt-12 px-4 max-w-4xl mx-auto">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
                {homepageSettings?.hero?.stats ? (
                  homepageSettings.hero.stats.map((stat, idx) => (
                    <div key={idx} className="bg-white/10 backdrop-blur-sm rounded-lg p-4 text-center border border-white/20">
                      <div className="text-2xl md:text-3xl font-bold text-white mb-1">{stat.value}</div>
                      <div className="text-xs md:text-sm text-white/70">{stat.label}</div>
                    </div>
                  ))
                ) : (
                  [
                    { value: '2,000+', label: 'Güncel Soru' },
                    { value: '70', label: 'Gerçek Sınav' },
                    { value: '%95', label: 'Başarı Oranı' },
                    { value: '🇹🇷', label: 'Türkçe Destek' }
                  ].map((stat, idx) => (
                    <div key={idx} className="bg-white/10 backdrop-blur-sm rounded-lg p-4 text-center border border-white/20">
                      <div className="text-2xl md:text-3xl font-bold text-white mb-1">{stat.value}</div>
                      <div className="text-xs md:text-sm text-white/70">{stat.label}</div>
                    </div>
                  ))
                )}
              </div>
            </div>
            
            {/* Trust Badges - Clean and Simple */}
            <div className="flex flex-wrap gap-4 md:gap-6 justify-center items-center mt-6 md:mt-8 text-white/70 px-4">
              <div className="flex items-center gap-2 text-xs md:text-sm font-medium">
                <CheckCircle className="w-4 h-4 flex-shrink-0" />
                <span>Trafikverket</span>
              </div>
              <div className="flex items-center gap-2 text-xs md:text-sm font-medium">
                <Users className="w-4 h-4 flex-shrink-0" />
                <span>1000+ Kullanıcı</span>
              </div>
              <div className="flex items-center gap-2 text-xs md:text-sm font-medium">
                <Trophy className="w-4 h-4 flex-shrink-0" />
                <span>%95 Başarı Oranı</span>
              </div>
            </div>
          </div>

          {/* CTA Buttons - Red Theme */}
          <div id="quiz-section">
          {!showQuestions ? (
            <div className="max-w-4xl mx-auto text-center space-y-8">
              
              {/* Free Trial Button */}
              <div className="relative">
                <div className="absolute -inset-4 bg-gradient-to-r from-yellow-400 via-orange-500 to-red-500 rounded-3xl blur-2xl opacity-75 animate-pulse"></div>
                
                <div className="relative bg-white rounded-3xl p-8 shadow-2xl">
                  <div className="text-center mb-6">
                    <div className="inline-flex items-center gap-3 mb-4">
                      <div className="w-16 h-16 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-full flex items-center justify-center shadow-lg">
                        <Trophy className="w-10 h-10 text-white" />
                      </div>
                      <div className="text-left">
                        <div className="text-6xl font-black bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent">
                          {freeQuestionCount}
                        </div>
                        <div className="text-sm font-bold text-gray-600">ÜCRETSİZ SORU</div>
                      </div>
                    </div>
                    
                    <h3 className="text-3xl font-black text-gray-900 mb-3">
                      Ücretsiz Deneyin!
                    </h3>
                    <p className="text-lg text-gray-600 mb-2">
                      Hiç ödeme yapmadan önce sistemi test edin
                    </p>
                  </div>

                  <button
                    onClick={() => setShowQuestions(true)}
                    className="w-full bg-gradient-to-r from-emerald-500 via-teal-500 to-emerald-500 text-white px-12 py-8 rounded-2xl font-black text-3xl hover:shadow-2xl transition-all duration-300 hover:scale-105 shadow-lg shadow-emerald-500/30"
                  >
                    <span className="flex items-center justify-center gap-4">
                      <Play className="w-10 h-10" />
                      <span>Ücretsiz Teste Başla</span>
                      <ArrowRight className="w-10 h-10" />
                    </span>
                  </button>
                </div>
              </div>

              {/* Separator */}
              <div className="flex items-center gap-4">
                <div className="flex-1 h-px bg-white/30"></div>
                <span className="text-white/60 font-semibold">VEYA</span>
                <div className="flex-1 h-px bg-white/30"></div>
              </div>
              
              <div className="relative group">
                <div className="absolute -inset-1 bg-gradient-to-r from-red-600 via-red-500 to-red-600 rounded-3xl blur opacity-75 group-hover:opacity-100 transition duration-300"></div>
                <button
                  onClick={onLogin}
                  className="relative w-full bg-red-600 text-white px-10 py-6 rounded-3xl font-black text-2xl hover:bg-red-700 transition-all duration-300 flex items-center justify-center gap-4 shadow-2xl"
                >
                  <LogIn className="w-8 h-8" />
                  <span>Giriş Yap & Premium'a Geç</span>
                  <Award className="w-8 h-8" />
                </button>
              </div>
              
              <div className="bg-white/20 backdrop-blur-sm rounded-2xl p-4 border border-white/30">
                <p className="text-white font-bold text-lg">
                  💎 <span className="font-bold text-green-400">{freeQuestionCount} ÜCRETSİZ SORU</span> + Premium: {homepageSettings?.hero?.total_questions_display || totalQuestions + '+'} Güncel Soru!
                </p>
              </div>
            </div>
          ) : showResults ? (
            // MEGA ENHANCED Results Screen - TAMAMEN YENİ TASARIM
            <div className="max-w-6xl mx-auto">
              {(() => {
                const results = calculateResults();
                const percentage = ((results.correct / results.total) * 100).toFixed(0);
                const passed = percentage >= 80;
                const good = percentage >= 60;

                return (
                  <div className="relative">
                    {/* Animated Background Blobs */}
                    <div className="absolute inset-0 overflow-hidden rounded-3xl">
                      <div className="absolute w-72 h-72 bg-gradient-to-br from-yellow-400/30 to-orange-500/30 rounded-full blur-3xl -top-20 -left-20 animate-blob"></div>
                      <div className="absolute w-72 h-72 bg-gradient-to-br from-pink-400/30 to-red-500/30 rounded-full blur-3xl -bottom-20 -right-20 animate-blob animation-delay-2000"></div>
                      <div className="absolute w-72 h-72 bg-gradient-to-br from-purple-400/30 to-blue-500/30 rounded-full blur-3xl top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 animate-blob animation-delay-4000"></div>
                    </div>

                    {/* Main Container */}
                    <div className="relative bg-white/95 backdrop-blur-xl rounded-3xl shadow-2xl overflow-hidden">
                      {/* Top Decorative Bar */}
                      <div className={`h-3 ${
                        passed ? 'bg-gradient-to-r from-green-400 via-emerald-500 to-teal-400' :
                        good ? 'bg-gradient-to-r from-yellow-400 via-orange-500 to-amber-400' :
                        'bg-gradient-to-r from-orange-400 via-red-500 to-pink-400'
                      }`}></div>

                      <div className="p-12">
                        {/* Hero Section */}
                        <div className="text-center mb-12">
                          {/* Animated Icon */}
                          <div className="relative inline-block mb-8">
                            <div className={`absolute inset-0 ${
                              passed ? 'bg-green-400' : good ? 'bg-yellow-400' : 'bg-orange-400'
                            } rounded-full blur-2xl opacity-50 animate-pulse`}></div>
                            <div className={`relative w-40 h-40 rounded-full flex items-center justify-center ${
                              passed ? 'bg-gradient-to-br from-green-400 via-emerald-500 to-teal-500' :
                              good ? 'bg-gradient-to-br from-yellow-400 via-orange-500 to-amber-500' :
                              'bg-gradient-to-br from-orange-400 via-red-500 to-pink-500'
                            } shadow-2xl transform hover:scale-110 transition-transform duration-300`}>
                              {passed ? (
                                <Trophy className="w-20 h-20 text-white animate-bounce" />
                              ) : good ? (
                                <Star className="w-20 h-20 text-white animate-spin-slow" />
                              ) : (
                                <Target className="w-20 h-20 text-white animate-pulse" />
                              )}
                            </div>
                          </div>

                          {/* Title */}
                          <h2 className={`text-6xl font-black mb-4 ${
                            passed ? 'text-transparent bg-clip-text bg-gradient-to-r from-green-600 to-emerald-600' :
                            good ? 'text-transparent bg-clip-text bg-gradient-to-r from-yellow-600 to-orange-600' :
                            'text-transparent bg-clip-text bg-gradient-to-r from-orange-600 to-red-600'
                          }`}>
                            {passed ? 'Harika! 🎉' : good ? 'İyi Bir Deneme! 💪' : 'Güzel Başlangıç! 🚀'}
                          </h2>

                          {/* Subtitle */}
                          <p className="text-2xl text-gray-600 font-semibold max-w-2xl mx-auto">
                            {passed 
                              ? 'Mükemmel performans! Sınava hazırsınız.' 
                              : good
                              ? 'İyi gidiyorsunuz! Biraz daha pratikle harika olacak.'
                              : 'Başlangıç için güzel! Pratik yaparak çok daha iyi olacaksınız.'}
                          </p>
                        </div>

                        {/* Stats Cards - BIG & BOLD */}
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
                          {/* Doğru Card */}
                          <div className="relative group">
                            <div className="absolute -inset-1 bg-gradient-to-r from-green-400 to-emerald-500 rounded-3xl blur opacity-25 group-hover:opacity-75 transition duration-300"></div>
                            <div className="relative bg-gradient-to-br from-green-50 to-emerald-50 rounded-3xl p-8 border-2 border-green-500 transform hover:scale-105 transition-all duration-300">
                              <div className="flex items-center justify-between mb-4">
                                <CheckCircle className="w-12 h-12 text-green-600" />
                                <div className="text-7xl font-black text-green-600">{results.correct}</div>
                              </div>
                              <div className="text-green-800 font-bold text-xl">✅ Doğru Cevap</div>
                            </div>
                          </div>

                          {/* Yanlış Card */}
                          <div className="relative group">
                            <div className="absolute -inset-1 bg-gradient-to-r from-red-400 to-pink-500 rounded-3xl blur opacity-25 group-hover:opacity-75 transition duration-300"></div>
                            <div className="relative bg-gradient-to-br from-red-50 to-pink-50 rounded-3xl p-8 border-2 border-red-500 transform hover:scale-105 transition-all duration-300">
                              <div className="flex items-center justify-between mb-4">
                                <div className="w-12 h-12 rounded-full bg-red-500 flex items-center justify-center">
                                  <span className="text-white font-black text-2xl">✗</span>
                                </div>
                                <div className="text-7xl font-black text-red-600">{results.wrong}</div>
                              </div>
                              <div className="text-red-800 font-bold text-xl">❌ Yanlış Cevap</div>
                            </div>
                          </div>

                          {/* Başarı Oranı Card */}
                          <div className="relative group">
                            <div className={`absolute -inset-1 ${
                              passed ? 'bg-gradient-to-r from-yellow-400 to-orange-500' :
                              'bg-gradient-to-r from-orange-400 to-red-500'
                            } rounded-3xl blur opacity-25 group-hover:opacity-75 transition duration-300`}></div>
                            <div className={`relative ${
                              passed ? 'bg-gradient-to-br from-yellow-50 to-orange-50 border-yellow-500' :
                              'bg-gradient-to-br from-orange-50 to-amber-50 border-orange-500'
                            } rounded-3xl p-8 border-2 transform hover:scale-105 transition-all duration-300`}>
                              <div className="flex items-center justify-between mb-4">
                                <Trophy className={`w-12 h-12 ${passed ? 'text-yellow-600' : 'text-orange-600'}`} />
                                <div className={`text-7xl font-black ${passed ? 'text-yellow-600' : 'text-orange-600'}`}>
                                  {percentage}%
                                </div>
                              </div>
                              <div className={`${passed ? 'text-yellow-800' : 'text-orange-800'} font-bold text-xl`}>
                                🎯 Başarı Oranı
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Progress Bar - MEGA */}
                        <div className="mb-12">
                          <div className="relative">
                            <div className="absolute -inset-2 bg-gradient-to-r from-purple-400 via-pink-500 to-red-500 rounded-full blur opacity-30"></div>
                            <div className="relative bg-gray-200 rounded-full h-8 overflow-hidden shadow-inner">
                              <div
                                className={`h-8 rounded-full transition-all duration-2000 ease-out ${
                                  passed ? 'bg-gradient-to-r from-green-400 via-emerald-500 to-teal-500' :
                                  good ? 'bg-gradient-to-r from-yellow-400 via-orange-500 to-amber-500' :
                                  'bg-gradient-to-r from-orange-400 via-red-500 to-pink-500'
                                } shadow-lg relative overflow-hidden`}
                                style={{ width: `${percentage}%` }}
                              >
                                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent animate-shimmer"></div>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Info Box - Sınav Bilgisi */}
                        <div className="bg-gradient-to-r from-blue-50 to-cyan-50 border-2 border-blue-500 rounded-2xl p-8 mb-12 shadow-lg">
                          <div className="flex items-start gap-6">
                            <div className="flex-shrink-0">
                              <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-2xl flex items-center justify-center shadow-lg transform hover:rotate-12 transition-transform">
                                <BookOpen className="w-8 h-8 text-white" />
                              </div>
                            </div>
                            <div className="flex-1">
                              <h3 className="text-2xl font-black text-blue-900 mb-3 flex items-center gap-2">
                                📚 Gerçek Sınavda Geçme Notu
                              </h3>
                              <p className="text-blue-800 text-lg leading-relaxed">
                                İsveç teoriprov sınavında başarılı olmak için <span className="px-3 py-1 bg-blue-500 text-white rounded-lg font-black">70 sorunun 52'sini</span> doğru cevaplamanız gerekir 
                                <span className="text-2xl font-black text-blue-900"> (≈%74)</span>
                              </p>
                              <div className="mt-4 flex items-center gap-3">
                                {percentage >= 74 ? (
                                  <div className="flex items-center gap-2 bg-green-500 text-white px-4 py-2 rounded-lg font-bold">
                                    <CheckCircle className="w-5 h-5" />
                                    Sınav seviyesinin üzerindesiniz!
                                  </div>
                                ) : (
                                  <div className="flex items-center gap-2 bg-orange-500 text-white px-4 py-2 rounded-lg font-bold">
                                    <Target className="w-5 h-5" />
                                    %{74 - percentage} daha pratik yapın!
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Action Buttons - MEGA */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          {/* Tekrar Dene */}
                          <button
                            onClick={restartQuiz}
                            className="relative group bg-white border-4 border-gray-300 hover:border-gray-400 text-gray-900 px-8 py-6 rounded-2xl font-black text-xl transition-all duration-300 hover:scale-105 shadow-xl overflow-hidden"
                          >
                            <div className="absolute inset-0 bg-gradient-to-r from-gray-100 to-gray-200 opacity-0 group-hover:opacity-100 transition-opacity"></div>
                            <div className="relative flex items-center justify-center gap-3">
                              <div className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center group-hover:rotate-180 transition-transform duration-500">
                                <span className="text-2xl">🔄</span>
                              </div>
                              <span>Tekrar Dene</span>
                            </div>
                          </button>

                          {/* Giriş Yap */}
                          <button
                            onClick={onLogin}
                            className="relative group bg-gradient-to-r from-red-600 via-orange-600 to-red-600 text-white px-8 py-6 rounded-2xl font-black text-xl transition-all duration-300 hover:scale-105 shadow-2xl overflow-hidden"
                          >
                            <div className="absolute inset-0 bg-gradient-to-r from-yellow-400 via-red-500 to-pink-500 opacity-0 group-hover:opacity-100 transition-opacity"></div>
                            <div className="relative flex items-center justify-center gap-3">
                              <LogIn className="w-6 h-6 group-hover:translate-x-1 transition-transform" />
                              <span>{totalQuestions} Soruya Tam Erişim!</span>
                              <Zap className="w-6 h-6 animate-pulse" />
                            </div>
                          </button>
                        </div>

                        {/* Bottom CTA */}
                        <div className="mt-8 text-center">
                          <p className="text-gray-600 text-lg">
                            🎓 <strong className="text-red-600">{results.total - results.correct}</strong> yanlış sorunuzu tekrar edin ve <strong className="text-green-600">%100</strong> başarıya ulaşın!
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })()}
            </div>
          ) : (
            // Trial Questions Section
            <div className="max-w-4xl mx-auto px-2 sm:px-4" ref={quizContainerRef}>
              <div className="bg-white/95 backdrop-blur-sm rounded-2xl sm:rounded-3xl p-4 sm:p-6 md:p-8 shadow-2xl">
                {/* Header with Marked Questions Button */}
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-4 sm:mb-6">
                  <div className="inline-flex items-center gap-2 bg-red-600 text-white px-3 sm:px-6 py-2 sm:py-3 rounded-full font-bold text-sm sm:text-lg">
                    <Play className="w-4 h-4 sm:w-5 sm:h-5" />
                    <span className="whitespace-nowrap">Soru {currentQuestion + 1} / {activeQuestions.length}</span>
                  </div>
                  
                  {markedQuestions.length > 0 && (
                    <button
                      onClick={() => setShowMarkedList(true)}
                      className="flex items-center gap-2 bg-yellow-500 hover:bg-yellow-600 text-white px-3 sm:px-4 py-2 sm:py-3 rounded-xl font-bold transition-all shadow-lg text-sm sm:text-base"
                    >
                      <Bookmark className="w-4 h-4 sm:w-5 sm:h-5 fill-current" />
                      <span>İşaretliler ({markedQuestions.length})</span>
                    </button>
                  )}
                </div>

                {/* Question - Protected from copying */}
                <div className="mb-4 sm:mb-6 select-none" style={{userSelect: 'none', WebkitUserSelect: 'none', MozUserSelect: 'none', msUserSelect: 'none'}}>
                  {/* Question ID Badge */}
                  <div className="flex items-center gap-2 mb-2 sm:mb-3 flex-wrap">
                    <span className="bg-gradient-to-r from-purple-600 to-purple-700 text-white px-2 sm:px-3 py-1 rounded-lg font-bold text-xs sm:text-sm">
                      {question.question_number || `#${question.id?.substring(0, 8) || `S-${String(currentQuestion + 1).padStart(3, '0')}`}`}
                    </span>
                    <span className="text-gray-500 text-xs sm:text-sm">
                      Soru {currentQuestion + 1} / {activeQuestions.length}
                    </span>
                  </div>
                  
                  <div className="flex flex-col sm:flex-row items-start gap-3 mb-3 sm:mb-4">
                    <h3 className="flex-1 text-base sm:text-lg md:text-xl font-bold text-gray-900 select-none leading-relaxed" onContextMenu={(e) => e.preventDefault()}>{question.question_text}</h3>
                    <button
                      onClick={() => toggleBookmark(question.id)}
                      className={`flex-shrink-0 flex items-center gap-1 sm:gap-2 px-3 sm:px-4 py-2 rounded-lg font-semibold transition-all text-sm ${
                        isMarked(question.id)
                          ? 'bg-yellow-500 text-white hover:bg-yellow-600 shadow-lg'
                          : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                      }`}
                      title={isMarked(question.id) ? 'İşareti kaldır' : 'İşaretle'}
                    >
                      <Bookmark className={`w-4 h-4 sm:w-5 sm:h-5 ${isMarked(question.id) ? 'fill-current' : ''}`} />
                      <span className="hidden sm:inline">{isMarked(question.id) ? 'İşaretlendi' : 'İşaretle'}</span>
                    </button>
                  </div>
                  
                  {/* Question Image - Professional Display with Zoom */}
                  {question.image_url && (
                    <div className="mb-4 flex justify-center">
                      <div className="relative bg-white rounded-xl p-2 shadow-md border border-gray-200 inline-block max-w-md">
                        <ImageLightbox
                          src={fixImageUrl(question.image_url)}
                          alt="Soru görseli"
                          className="rounded-lg max-h-48 w-auto object-contain"
                          containerClassName="relative"
                        />
                      </div>
                    </div>
                  )}
                  
                  {/* Multiple Images Display */}
                  {question.image_urls && question.image_urls.length > 0 && (
                    <div className="mb-4">
                      <div className="flex flex-wrap justify-center gap-3">
                        {question.image_urls.map((url, index) => (
                          <div key={index} className="relative bg-white rounded-xl p-2 shadow-md border border-gray-200">
                            <ImageLightbox
                              src={fixImageUrl(url)}
                              alt={`Ek görsel ${index + 1}`}
                              className="rounded-lg max-h-36 w-auto object-contain"
                              containerClassName="relative"
                            />
                            <span className="absolute bottom-1 left-1 bg-black/70 text-white text-xs px-2 py-1 rounded">
                              #{index + 1}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  <div className="space-y-2 sm:space-y-3 select-none" onContextMenu={(e) => e.preventDefault()}>
                    {question.choices.map((choice) => {
                      const isSelected = userAnswer === choice.letter;
                      const isCorrectChoice = choice.letter === question.correct_answer;
                      const showCorrect = showExplanation && isCorrectChoice;
                      const showWrong = showExplanation && isSelected && !isCorrectChoice;

                      return (
                        <button
                          key={choice.letter}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            if (!showExplanation) {
                              handleAnswer(choice.letter);
                            }
                          }}
                          disabled={showExplanation}
                          className={`w-full text-left px-3 sm:px-5 md:px-6 py-3 sm:py-4 rounded-xl font-medium sm:font-semibold transition-all text-sm sm:text-base ${
                            showCorrect
                              ? 'bg-green-100 border-2 border-green-500 text-green-900'
                              : showWrong
                              ? 'bg-red-100 border-2 border-red-500 text-red-900'
                              : isSelected
                              ? 'bg-red-600 text-white shadow-lg'
                              : 'bg-gray-100 text-gray-900 hover:bg-gray-200 border border-gray-300'
                          } ${showExplanation ? 'cursor-not-allowed' : ''}`}
                        >
                          <span className="font-bold">{choice.letter}.</span>
                          <span className="ml-2">{choice.text}</span>
                          {showCorrect && <CheckCircle className="inline-block w-4 h-4 sm:w-5 sm:h-5 ml-2 text-green-600" />}
                        </button>
                      );
                    })}
                  </div>

                  {showExplanation && (
                    <div className={`mt-4 sm:mt-6 p-4 sm:p-6 rounded-xl ${
                      isCorrect ? 'bg-green-50 border-2 border-green-500' : 'bg-red-50 border-2 border-red-500'
                    }`}>
                      <div className="flex items-center gap-2 mb-2">
                        {isCorrect ? (
                          <CheckCircle className="w-5 h-5 sm:w-6 sm:h-6 text-green-600" />
                        ) : (
                          <div className="w-5 h-5 sm:w-6 sm:h-6 text-red-600 font-bold text-lg sm:text-xl">✗</div>
                        )}
                        <span className={`font-bold text-base sm:text-lg ${isCorrect ? 'text-green-900' : 'text-red-900'}`}>
                          {isCorrect ? 'Doğru!' : 'Yanlış'}
                        </span>
                      </div>
                      <div className="text-sm sm:text-base text-gray-800" style={{ whiteSpace: 'pre-wrap' }}>
                        <strong>Açıklama:</strong> {question.explanation}
                      </div>
                    </div>
                  )}
                </div>

                {/* Navigation */}
                <div className="space-y-3">
                  {/* Progress Bar */}
                  <div className="mb-3 sm:mb-4">
                    <div className="flex justify-between text-xs sm:text-sm text-gray-600 mb-1">
                      <span>İlerleme</span>
                      <span>{Object.keys(userAnswers).length} / {activeQuestions.length}</span>
                    </div>
                    <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-gradient-to-r from-red-500 to-orange-500 transition-all duration-300"
                        style={{ width: `${(Object.keys(userAnswers).length / activeQuestions.length) * 100}%` }}
                      ></div>
                    </div>
                  </div>

                  <div className="flex gap-2 sm:gap-4">
                    <button
                      onClick={prevQuestion}
                      disabled={currentQuestion === 0}
                      className="flex-1 bg-gray-200 text-gray-700 px-3 sm:px-6 py-2.5 sm:py-3 rounded-xl font-medium sm:font-semibold hover:bg-gray-300 transition-all disabled:opacity-50 disabled:cursor-not-allowed text-sm sm:text-base"
                    >
                      ← <span className="hidden sm:inline">Önceki</span>
                    </button>
                    {currentQuestion === activeQuestions.length - 1 ? (
                      <button
                        onClick={nextQuestion}
                        disabled={!showExplanation}
                        className="flex-2 bg-gradient-to-r from-green-600 to-emerald-600 text-white px-4 sm:px-8 py-2.5 sm:py-3 rounded-xl font-bold hover:from-green-700 hover:to-emerald-700 transition-all hover:scale-105 flex items-center justify-center gap-1 sm:gap-2 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed text-sm sm:text-base"
                      >
                        <Trophy className="w-4 h-4 sm:w-5 sm:h-5" />
                        <span className="hidden sm:inline">Sonuçları Gör</span>
                        <span className="sm:hidden">Sonuç</span>
                        <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5" />
                      </button>
                    ) : (
                      <button
                        onClick={nextQuestion}
                        disabled={!showExplanation}
                        className="flex-1 bg-red-600 text-white px-3 sm:px-6 py-2.5 sm:py-3 rounded-xl font-medium sm:font-semibold hover:bg-red-700 transition-all hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg text-sm sm:text-base"
                      >
                        <span className="hidden sm:inline">Sonraki</span> →
                      </button>
                    )}
                  </div>
                  <div className="flex justify-center">
                    <ErrorReportButton question={activeQuestions[currentQuestion]} />
                  </div>
                  
                  {/* Genel Bakış ve Sınavı Bitir Butonları */}
                  <div className="flex gap-2 sm:gap-4 mt-3 sm:mt-4">
                    <button
                      onClick={toggleOverview}
                      className="flex-1 bg-blue-600 text-white px-3 sm:px-6 py-2.5 sm:py-3 rounded-xl font-medium sm:font-semibold hover:bg-blue-700 transition-all flex items-center justify-center gap-1 sm:gap-2 text-sm sm:text-base"
                    >
                      <BarChart3 className="w-4 h-4 sm:w-5 sm:h-5" />
                      <span className="hidden sm:inline">Genel Bakış</span>
                      <span className="sm:hidden">Bakış</span>
                    </button>
                    <button
                      onClick={finishQuiz}
                      className="flex-1 bg-gradient-to-r from-green-600 to-emerald-600 text-white px-3 sm:px-6 py-2.5 sm:py-3 rounded-xl font-medium sm:font-semibold hover:from-green-700 hover:to-emerald-700 transition-all flex items-center justify-center gap-1 sm:gap-2 shadow-lg text-sm sm:text-base"
                    >
                      <Trophy className="w-4 h-4 sm:w-5 sm:h-5" />
                      <span className="hidden sm:inline">Sınavı Bitir</span>
                      <span className="sm:hidden">Bitir</span>
                    </button>
                  </div>
                </div>
              </div>

              {/* Genel Bakış Modal */}
              {showOverview && (
                <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-2 sm:p-4">
                  <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden shadow-2xl">
                    {/* Modal Header */}
                    <div className="bg-gradient-to-r from-blue-600 to-indigo-600 p-4 sm:p-6 flex items-center justify-between">
                      <div className="flex items-center gap-2 sm:gap-3">
                        <BarChart3 className="w-6 h-6 sm:w-8 sm:h-8 text-white" />
                        <h3 className="text-xl sm:text-2xl font-black text-white">Genel Bakış</h3>
                      </div>
                      <button
                        onClick={() => setShowOverview(false)}
                        className="bg-white/20 hover:bg-white/30 text-white p-2 rounded-lg transition-all"
                      >
                        <X className="w-5 h-5 sm:w-6 sm:h-6" />
                      </button>
                    </div>

                    {/* Progress Summary */}
                    <div className="p-3 sm:p-4 bg-gray-50 border-b">
                      <div className="flex justify-around text-center">
                        <div>
                          <div className="text-3xl font-bold text-green-600">{Object.values(userAnswers).filter((ans, idx) => ans === activeQuestions[idx]?.correct_answer).length}</div>
                          <div className="text-sm text-gray-600">Doğru</div>
                        </div>
                        <div>
                          <div className="text-3xl font-bold text-red-600">{Object.values(userAnswers).filter((ans, idx) => ans && ans !== activeQuestions[idx]?.correct_answer).length}</div>
                          <div className="text-sm text-gray-600">Yanlış</div>
                        </div>
                        <div>
                          <div className="text-3xl font-bold text-gray-500">{activeQuestions.length - Object.keys(userAnswers).length}</div>
                          <div className="text-sm text-gray-600">Boş</div>
                        </div>
                      </div>
                    </div>

                    {/* Questions Grid */}
                    <div className="p-6 overflow-y-auto max-h-[50vh]">
                      <div className="grid grid-cols-5 md:grid-cols-8 lg:grid-cols-10 gap-3">
                        {activeQuestions.map((q, idx) => {
                          const answered = userAnswers[idx];
                          const isCorrect = answered === q.correct_answer;
                          const isCurrent = idx === currentQuestion;
                          
                          return (
                            <button
                              key={idx}
                              onClick={() => goToQuestion(idx)}
                              className={`aspect-square rounded-xl font-bold text-lg transition-all hover:scale-110 flex items-center justify-center ${
                                isCurrent
                                  ? 'ring-4 ring-blue-500 ring-offset-2'
                                  : ''
                              } ${
                                !answered
                                  ? 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                                  : isCorrect
                                  ? 'bg-green-500 text-white'
                                  : 'bg-red-500 text-white'
                              }`}
                            >
                              {idx + 1}
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    {/* Legend */}
                    <div className="p-4 bg-gray-50 border-t flex justify-center gap-6">
                      <div className="flex items-center gap-2">
                        <div className="w-6 h-6 rounded bg-green-500"></div>
                        <span className="text-sm text-gray-600">Doğru</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-6 h-6 rounded bg-red-500"></div>
                        <span className="text-sm text-gray-600">Yanlış</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-6 h-6 rounded bg-gray-200"></div>
                        <span className="text-sm text-gray-600">Boş</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-6 h-6 rounded bg-blue-500"></div>
                        <span className="text-sm text-gray-600">Şu an</span>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="p-4 border-t flex gap-4">
                      <button
                        onClick={() => setShowOverview(false)}
                        className="flex-1 bg-gray-200 text-gray-700 px-6 py-3 rounded-xl font-semibold hover:bg-gray-300 transition-all"
                      >
                        Devam Et
                      </button>
                      <button
                        onClick={finishQuiz}
                        className="flex-1 bg-gradient-to-r from-green-600 to-emerald-600 text-white px-6 py-3 rounded-xl font-bold hover:from-green-700 hover:to-emerald-700 transition-all shadow-lg"
                      >
                        <span className="flex items-center justify-center gap-2">
                          <Trophy className="w-5 h-5" />
                          Sınavı Bitir
                        </span>
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Marked Questions Modal */}
              {showMarkedList && (
                <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
                  <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[80vh] overflow-hidden shadow-2xl">
                    {/* Modal Header */}
                    <div className="bg-gradient-to-r from-yellow-500 to-orange-500 p-6 flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <Bookmark className="w-8 h-8 text-white fill-current" />
                        <h3 className="text-2xl font-black text-white">İşaretli Sorular ({markedQuestions.length})</h3>
                      </div>
                      <button
                        onClick={() => setShowMarkedList(false)}
                        className="bg-white/20 hover:bg-white/30 text-white p-2 rounded-lg transition-all"
                      >
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>

                    {/* Modal Body */}
                    <div className="p-6 overflow-y-auto max-h-[60vh]">
                      {markedQuestions.length === 0 ? (
                        <div className="text-center py-12">
                          <Bookmark className="w-20 h-20 text-gray-300 mx-auto mb-4" />
                          <p className="text-gray-500 text-lg">Henüz işaretli soru yok</p>
                        </div>
                      ) : (
                        <div className="space-y-3">
                          {markedQuestions.map((qId) => {
                            const q = SAMPLE_QUESTIONS.find(sq => sq.id === qId);
                            if (!q) return null;
                            const qIndex = SAMPLE_QUESTIONS.indexOf(q);
                            const isCurrentQuestion = qIndex === currentQuestion;
                            
                            return (
                              <button
                                key={qId}
                                onClick={() => {
                                  setCurrentQuestion(qIndex);
                                  setShowExplanation(false);
                                  setShowMarkedList(false);
                                }}
                                className={`w-full text-left p-4 rounded-xl transition-all ${
                                  isCurrentQuestion
                                    ? 'bg-red-100 border-2 border-red-500'
                                    : 'bg-gray-50 hover:bg-gray-100 border border-gray-200'
                                }`}
                              >
                                <div className="flex items-start gap-3">
                                  <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center font-bold ${
                                    isCurrentQuestion ? 'bg-red-500 text-white' : 'bg-gray-300 text-gray-700'
                                  }`}>
                                    {qIndex + 1}
                                  </div>
                                  <div className="flex-1">
                                    <p className={`font-semibold ${isCurrentQuestion ? 'text-red-900' : 'text-gray-900'}`}>
                                      {q.question_text}
                                    </p>
                                    {userAnswers[qIndex] && (
                                      <div className="mt-2 flex items-center gap-2">
                                        {userAnswers[qIndex] === q.correct_answer ? (
                                          <span className="text-green-600 text-sm font-semibold flex items-center gap-1">
                                            <CheckCircle className="w-4 h-4" />
                                            Doğru cevaplandı
                                          </span>
                                        ) : (
                                          <span className="text-red-600 text-sm font-semibold flex items-center gap-1">
                                            <span className="w-4 h-4 rounded-full bg-red-500 flex items-center justify-center text-white text-xs">✗</span>
                                            Yanlış cevaplandı
                                          </span>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                  {isCurrentQuestion && (
                                    <div className="flex-shrink-0 text-red-500 font-bold text-sm">
                                      Şu an bu soru
                                    </div>
                                  )}
                                </div>
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </div>

                    {/* Modal Footer */}
                    <div className="bg-gray-50 p-4 flex justify-end">
                      <button
                        onClick={() => setShowMarkedList(false)}
                        className="bg-gray-800 hover:bg-gray-900 text-white px-6 py-2 rounded-lg font-semibold transition-all"
                      >
                        Kapat
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Pricing Section - Hero'nun hemen altında */}
      <PricingSection onSelectPackage={() => window.location.href = '/payment'} />

      {/* HIDDEN SECTIONS - Sürekli Güncel & Neden Ehliyet.nu - Kullanıcı isteğiyle kaldırıldı (13 Ocak 2026) */}
      {/* Geri eklemek için bu bölümü açınız - hidden_sections_backup.txt dosyasında yedek var */}
      {/* Blog / Rehber Bölümü - SEO için önemli */}
      <div className="py-12 md:py-20" style={{backgroundColor: '#ffffff'}}>
        <div className="container mx-auto px-4">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-black text-gray-900 text-center mb-3 md:mb-4">
            📚 Körkort Teori Rehberi - İsveç Ehliyet
          </h2>
          <p className="text-base md:text-xl text-gray-600 text-center mb-4 max-w-2xl mx-auto">
            Körkort frågor på turkiska - İsveç'te ehliyet almak isteyenler için rehberler
          </p>
          <p className="text-sm md:text-base text-gray-500 text-center mb-8 md:mb-12 max-w-3xl mx-auto">
            Teoriprov körkort turkiska | Hur klarar man teoriprovet i Sverige turkiska | Trafikskyltar sverige turkiska förklaring
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {/* Dynamic Blog Posts from API */}
            {blogPosts.length > 0 ? (
              blogPosts.map((post, index) => {
                // Color mapping based on category or index
                const colorSchemes = [
                  { gradient: 'from-red-600 to-orange-500', bg: 'bg-red-100', text: 'text-red-600', hover: 'group-hover:text-red-600' },
                  { gradient: 'from-blue-600 to-cyan-500', bg: 'bg-blue-100', text: 'text-blue-600', hover: 'group-hover:text-blue-600' },
                  { gradient: 'from-green-600 to-emerald-500', bg: 'bg-green-100', text: 'text-green-600', hover: 'group-hover:text-green-600' },
                  { gradient: 'from-purple-600 to-pink-500', bg: 'bg-purple-100', text: 'text-purple-600', hover: 'group-hover:text-purple-600' },
                  { gradient: 'from-yellow-500 to-orange-500', bg: 'bg-yellow-100', text: 'text-yellow-700', hover: 'group-hover:text-yellow-600' },
                  { gradient: 'from-indigo-600 to-violet-500', bg: 'bg-indigo-100', text: 'text-indigo-600', hover: 'group-hover:text-indigo-600' },
                ];
                const color = colorSchemes[index % colorSchemes.length];
                
                return (
                  <a 
                    key={post.id || index}
                    href={`/blog/${post.slug}`} 
                    className="group bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2"
                  >
                    <div className={`h-48 bg-gradient-to-br ${post.gradient || color.gradient} flex items-center justify-center`}>
                      <span className="text-6xl">{post.emoji || '📄'}</span>
                    </div>
                    <div className="p-6">
                      <div className="flex items-center gap-2 mb-3">
                        <span className={`px-3 py-1 ${color.bg} ${color.text} rounded-full text-xs font-bold uppercase`}>
                          {post.category || 'BLOG'}
                        </span>
                        <span className="text-gray-400 text-xs">{post.read_time || '5 dk okuma'}</span>
                      </div>
                      <h3 className={`text-xl font-black text-gray-900 mb-2 ${color.hover} transition-colors`}>
                        {post.title}
                      </h3>
                      <p className="text-gray-600 text-sm mb-4 line-clamp-2">
                        {post.excerpt || post.meta_description || 'Devamını okumak için tıklayın...'}
                      </p>
                      <span className={`${color.text} font-bold text-sm flex items-center gap-1 group-hover:gap-3 transition-all`}>
                        Devamını Oku <ArrowRight className="w-4 h-4" />
                      </span>
                    </div>
                  </a>
                );
              })
            ) : (
              // Fallback static content if no posts from API
              <>
                <a href="/blog/teori-sinavi-nasil-gecilir" className="group bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2">
                  <div className="h-48 bg-gradient-to-br from-red-600 to-orange-500 flex items-center justify-center">
                    <span className="text-6xl">📝</span>
                  </div>
                  <div className="p-6">
                    <div className="flex items-center gap-2 mb-3">
                      <span className="px-3 py-1 bg-red-100 text-red-600 rounded-full text-xs font-bold">SINAV</span>
                    </div>
                    <h3 className="text-xl font-black text-gray-900 mb-2 group-hover:text-red-600 transition-colors">
                      Teori Sınavı Nasıl Geçilir?
                    </h3>
                    <p className="text-gray-600 text-sm mb-4">Teoriprov körkort turkiska - sınav ipuçları...</p>
                    <span className="text-red-600 font-bold text-sm flex items-center gap-1">
                      Devamını Oku <ArrowRight className="w-4 h-4" />
                    </span>
                  </div>
                </a>
                <a href="/blog/isvec-trafik-isaretleri" className="group bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2">
                  <div className="h-48 bg-gradient-to-br from-blue-600 to-cyan-500 flex items-center justify-center">
                    <span className="text-6xl">🚦</span>
                  </div>
                  <div className="p-6">
                    <div className="flex items-center gap-2 mb-3">
                      <span className="px-3 py-1 bg-blue-100 text-blue-600 rounded-full text-xs font-bold">TRAFİK</span>
                    </div>
                    <h3 className="text-xl font-black text-gray-900 mb-2 group-hover:text-blue-600 transition-colors">
                      Trafikskyltar Sverige Turkiska
                    </h3>
                    <p className="text-gray-600 text-sm mb-4">İsveç trafik işaretleri Türkçe anlamları...</p>
                    <span className="text-blue-600 font-bold text-sm flex items-center gap-1">
                      Devamını Oku <ArrowRight className="w-4 h-4" />
                    </span>
                  </div>
                </a>
                <a href="/blog/isvecte-ehliyet-nasil-alinir" className="group bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2">
                  <div className="h-48 bg-gradient-to-br from-green-600 to-emerald-500 flex items-center justify-center">
                    <span className="text-6xl">🚗</span>
                  </div>
                  <div className="p-6">
                    <div className="flex items-center gap-2 mb-3">
                      <span className="px-3 py-1 bg-green-100 text-green-600 rounded-full text-xs font-bold">REHBER</span>
                    </div>
                    <h3 className="text-xl font-black text-gray-900 mb-2 group-hover:text-green-600 transition-colors">
                      İsveç'te Ehliyet Nasıl Alınır?
                    </h3>
                    <p className="text-gray-600 text-sm mb-4">Hur får man körkort i Sverige på turkiska...</p>
                    <span className="text-green-600 font-bold text-sm flex items-center gap-1">
                      Devamını Oku <ArrowRight className="w-4 h-4" />
                    </span>
                  </div>
                </a>
              </>
            )}
          </div>

          {/* Tüm Yazılar Butonu */}
          <div className="text-center mt-10">
            <a 
              href="/blog" 
              className="inline-flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-red-600 to-orange-500 text-white rounded-2xl font-bold text-lg hover:shadow-xl transition-all duration-300 hover:scale-105"
            >
              <BookOpen className="w-6 h-6" />
              Tüm Yazıları Görüntüle
              <ArrowRight className="w-5 h-5" />
            </a>
          </div>
        </div>
      </div>

      {/* Testimonials - Mobile Optimized */}
      <div className="py-12 md:py-20" style={{backgroundColor: '#ffffff'}}>
        <div className="container mx-auto px-4">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-black text-gray-900 text-center mb-3 md:mb-4">
            Kullanıcı Yorumları
          </h2>
          <p className="text-base md:text-xl text-gray-600 text-center mb-8 md:mb-16">
            Binlerce başarı hikayesinden bazıları
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-8 max-w-5xl mx-auto">
            {testimonials.map((testimonial, idx) => (
              <div
                key={idx}
                className="bg-gray-50 rounded-xl md:rounded-2xl p-6 md:p-8 shadow-lg hover:shadow-xl transition-all"
              >
                <div className="flex items-center gap-1 mb-3 md:mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="w-4 h-4 md:w-5 md:h-5 text-red-600 fill-red-600" />
                  ))}
                </div>
                <p className="text-gray-800 mb-4 md:mb-6 text-base md:text-lg italic leading-relaxed">"{testimonial.text}"</p>
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div>
                    <div className="text-gray-900 font-bold text-sm md:text-base">{testimonial.name}</div>
                    <div className="text-gray-500 text-xs md:text-sm">{testimonial.location}</div>
                  </div>
                  <div className="text-gray-400 text-xs md:text-sm">{testimonial.date}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Final CTA */}
      <div className="py-20" style={{backgroundColor: '#ffffff'}}>
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl md:text-5xl font-black mb-6 text-gray-900">
            Sınavda Başarılı Olmaya Hazır mısınız?
          </h2>
          <p className="text-xl mb-10 text-gray-600 max-w-2xl mx-auto">
            Hemen başlayın ve İsveç sürücü belgenizi ilk seferde alın!
          </p>
          
          <button
            onClick={onLogin}
            className="bg-gradient-to-r from-red-600 to-red-700 text-white px-12 py-6 rounded-2xl font-bold text-2xl hover:from-red-700 hover:to-red-800 transition-all duration-300 hover:scale-105 shadow-2xl inline-flex items-center gap-4"
          >
            <LogIn className="w-8 h-8" />
            <span>Hemen Başla</span>
            <ArrowRight className="w-8 h-8" />
          </button>
          
          <p className="text-gray-500 text-sm mt-6">
            ⚡ 2000+ güncel soru • 💳 Güvenli ödeme • 📱 Mobil uyumlu
          </p>
        </div>
      </div>
      </div>
      </div>

      {/* SEO Content Section - Visible text for search engines */}
      <section className="py-16" style={{backgroundColor: '#ffffff'}}>
        <div className="container mx-auto px-4">
          <article className="max-w-4xl mx-auto prose prose-lg">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              İsveç Ehliyet Sınavına Türkçe Hazırlanın
            </h2>
            <p className="text-gray-700 mb-6">
              Ehliyet.nu, İsveç'te yaşayan Türkler için özel olarak hazırlanmış kapsamlı bir ehliyet hazırlık platformudur. 
              2000'den fazla güncel soru ile teori sınavına (Teoriprov) tam hazırlık imkanı sunuyoruz. 
              Türkçe açıklamalar sayesinde trafik kurallarını kolayca öğrenin ve sınavı ilk seferde geçin.
            </p>
            
            <h3 className="text-2xl font-semibold text-gray-800 mb-4">
              Neden Ehliyet.nu?
            </h3>
            <ul className="list-disc list-inside text-gray-700 mb-6 space-y-2">
              <li><strong>Trafikverket Uyumlu:</strong> Tüm sorular İsveç Trafik Kurumu standartlarına uygun</li>
              <li><strong>%95 Başarı Oranı:</strong> Kullanıcılarımızın büyük çoğunluğu ilk seferde geçiyor</li>
              <li><strong>Türkçe Açıklamalar:</strong> Her sorunun detaylı Türkçe açıklaması mevcut</li>
              <li><strong>Gerçek Sınav Formatı:</strong> 70 soru, 50 dakika - gerçek sınav deneyimi</li>
              <li><strong>Mobil Uyumlu:</strong> Telefon, tablet veya bilgisayardan erişim</li>
            </ul>
            
            <h3 className="text-2xl font-semibold text-gray-800 mb-4">
              İsveç Ehliyet Sınavı Hakkında
            </h3>
            <p className="text-gray-700 mb-4">
              İsveç ehliyet teorik sınavı (Teoriprov) 70 sorudan oluşur. 
              Geçmek için en az 52 soruyu (%74) doğru cevaplamanız gerekir. 
              Sınav süresi 50 dakikadır ve Türkçe dahil 13 farklı dilde yapılabilir.
            </p>
            <p className="text-gray-700 mb-6">
              Teori sınavını geçtikten sonra pratik sürüş sınavına (Uppkörning) girebilirsiniz. 
              İsveç'te ehliyet alma süreci ortalama 3-6 ay sürmektedir.
            </p>
            
            <div className="bg-red-50 border-l-4 border-red-600 p-4 rounded-r-lg">
              <p className="text-gray-800 font-medium">
                💡 <strong>İpucu:</strong> Ücretsiz deneme sorularımızla hemen başlayın! 
                Premium üyelik ile 2000+ sorunun tamamına erişin ve sınav simülasyonu yapın.
              </p>
            </div>
          </article>
          
          {/* Internal Links for SEO */}
          <nav className="max-w-4xl mx-auto mt-12 pt-8 border-t border-gray-200" aria-label="Site navigation">
            <h3 className="text-xl font-semibold text-gray-800 mb-4">Faydalı Sayfalar</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <a href="/sss" className="text-red-600 hover:text-red-700 hover:underline font-medium">
                Sık Sorulan Sorular
              </a>
              <a href="/blog" className="text-red-600 hover:text-red-700 hover:underline font-medium">
                Ehliyet Rehberi Blog
              </a>
              <a href="/payment" className="text-red-600 hover:text-red-700 hover:underline font-medium">
                Premium Üyelik
              </a>
              <a href="/nasil-calisir" className="text-red-600 hover:text-red-700 hover:underline font-medium">
                Nasıl Çalışır?
              </a>
            </div>
          </nav>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white">
        <div className="container mx-auto px-4 py-12">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
            {/* Column 1 - Logo & Description */}
            <div>
              <div className="flex items-center gap-2 mb-4">
                <div className="w-10 h-10 bg-gradient-to-br from-red-600 to-blue-600 rounded-lg flex items-center justify-center font-black text-white">
                  E
                </div>
                <span className="text-2xl font-black text-white">
                  {footerSettings?.about_title || 'Ehliyet.nu'}
                </span>
              </div>
              <p className="text-white/70 text-sm leading-relaxed">
                {footerSettings?.about_text || "İsveç'te sürücü belgesi almak isteyen Türk adaylar için kapsamlı teoriprov hazırlık platformu."}
              </p>
            </div>

            {/* Column 2 - Hızlı Linkler (Yasal Linkler) */}
            <div>
              <h3 className="text-lg font-bold text-white mb-4">Hızlı Linkler</h3>
              <ul className="space-y-2 text-white/70 text-sm">
                <li>
                  <a href="/sss" className="hover:text-white transition-colors">
                    ❓ Sık Sorulan Sorular
                  </a>
                </li>
                <li>
                  <a href="/blog" className="hover:text-white transition-colors">
                    📝 Blog
                  </a>
                </li>
                <li>
                  <a href="/nasil-calisir" className="hover:text-white transition-colors">
                    📖 Nasıl Çalışır?
                  </a>
                </li>
                {/* Dynamic Legal Links from Admin */}
                {footerSettings?.legal_links?.filter(link => link.enabled).map((link, index) => (
                  <li key={index}>
                    <a href={link.url} className="hover:text-white transition-colors">
                      {link.name}
                    </a>
                  </li>
                ))}
                {/* Fallback if no footer settings */}
                {!footerSettings?.legal_links && (
                  <>
                    <li>
                      <a href="/gizlilik-politikasi" className="hover:text-white transition-colors">
                        🔒 Gizlilik Politikası
                      </a>
                    </li>
                    <li>
                      <a href="/kullanim-sartlari" className="hover:text-white transition-colors">
                        📋 Kullanım Şartları
                      </a>
                    </li>
                    <li>
                      <a href="/hakkimizda" className="hover:text-white transition-colors">
                        ℹ️ Hakkımızda
                      </a>
                    </li>
                  </>
                )}
                <li>
                  <a href="/iletisim" className="hover:text-white transition-colors">
                    ✉️ İletişim
                  </a>
                </li>
              </ul>
            </div>

            {/* Column 3 - Özellikler */}
            <div>
              <h3 className="text-lg font-bold text-white mb-4">Özellikler</h3>
              <ul className="space-y-2 text-white/70 text-sm">
                <li>🎯 {totalQuestions} Güncel Soru</li>
                <li>📚 Türkçe Açıklamalar</li>
                <li>✅ Trafikverket Uyumlu</li>
                <li>🏆 %95 Başarı Oranı</li>
                <li>📱 Mobil Uyumlu Platform</li>
              </ul>
            </div>

            {/* Column 4 - İletişim & Sosyal Medya */}
            <div>
              <h3 className="text-lg font-bold text-white mb-4">
                {footerSettings?.follow_title || 'İletişim'}
              </h3>
              <div className="space-y-3">
                {/* Dynamic Social Media Links from Admin */}
                {footerSettings?.social_media?.filter(sm => sm.enabled).map((social, index) => (
                  <a 
                    key={index}
                    href={social.url} 
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-white/70 hover:text-white transition-colors group"
                  >
                    <div className="w-8 h-8 bg-white/10 rounded-lg flex items-center justify-center group-hover:bg-red-600 transition-colors">
                      {social.icon === 'Mail' && <Mail className="w-4 h-4" />}
                      {social.icon === 'Facebook' && <Facebook className="w-4 h-4" />}
                      {social.icon === 'Instagram' && <Instagram className="w-4 h-4" />}
                      {social.icon === 'Twitter' && <Twitter className="w-4 h-4" />}
                      {social.icon === 'Linkedin' && <Linkedin className="w-4 h-4" />}
                    </div>
                    <span className="text-sm">{social.name}</span>
                  </a>
                ))}
                {/* Fallback if no footer settings */}
                {!footerSettings?.social_media && (
                  <a 
                    href="mailto:info@ehliyet.nu" 
                    className="flex items-center gap-2 text-white/70 hover:text-white transition-colors group"
                  >
                    <div className="w-8 h-8 bg-white/10 rounded-lg flex items-center justify-center group-hover:bg-red-600 transition-colors">
                      <Mail className="w-4 h-4" />
                    </div>
                    <span className="text-sm">info@ehliyet.nu</span>
                  </a>
                )}
                <p className="text-white/50 text-xs">
                  Sorularınız için bize yazın
                </p>
              </div>
            </div>
          </div>

          {/* Bottom Bar */}
          <div className="border-t border-white/10 pt-6">
            <div className="flex flex-col md:flex-row justify-between items-center gap-4 text-sm text-white/50">
              <p>{footerSettings?.copyright_text || '© Ehliyet.nu - Tüm hakları saklıdır'}</p>
              <p>{footerSettings?.made_with_text || 'Trafikverket standartlarında güncel içerik'}</p>
            </div>
          </div>
        </div>
      </footer>

      {/* Cookie Banner - GDPR Compliant */}
      {showCookieBanner && (
        <div className="fixed bottom-0 left-0 right-0 z-50 bg-gray-900 border-t-4 border-red-600 shadow-2xl animate-fade-in">
          <div className="container mx-auto px-4 py-6">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <div className="flex-1">
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-10 h-10 bg-red-600 rounded-lg flex items-center justify-center">
                    <Shield className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-white font-bold mb-1">🍪 Çerezler (Cookies)</h3>
                    <p className="text-white/70 text-sm">
                      Web sitemiz, deneyiminizi geliştirmek ve ilerlemenizi kaydetmek için çerezler kullanmaktadır. 
                      Detaylı bilgi için{' '}
                      <a href="/gizlilik-politikasi" className="text-amber-400 hover:underline">Gizlilik Politikamızı</a>{' '}
                      inceleyebilirsiniz.
                    </p>
                    <p className="text-white/50 text-xs mt-1">
                      Vi använder cookies för att förbättra din upplevelse. Läs vår{' '}
                      <a href="/gizlilik-politikasi" className="text-amber-400 hover:underline">integritetspolicy</a>.
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="flex gap-3">
                <button
                  onClick={acceptCookies}
                  className="bg-red-600 hover:bg-red-700 text-white px-8 py-3 rounded-xl font-bold transition-all hover:scale-105 shadow-lg"
                >
                  Kabul Et / Acceptera
                </button>
                <button
                  onClick={() => setShowCookieBanner(false)}
                  className="bg-white/10 hover:bg-white/20 text-white px-6 py-3 rounded-xl font-semibold transition-all"
                >
                  Reddet / Avvisa
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
